/**
 * Sales / ads tracking.
 *
 * Preferred setup: GTM container owns GA4, Meta Pixel, and Google Ads tags.
 * This module always pushes GA4 ecommerce events to window.dataLayer so GTM
 * can forward them. When GTM is not configured, it loads GA4 / Meta / Ads
 * scripts directly from env IDs so events still fire.
 *
 * Env:
 *   REACT_APP_GTM_ID
 *   REACT_APP_GA4_MEASUREMENT_ID (falls back to REACT_APP_FIREBASE_MEASUREMENT_ID)
 *   REACT_APP_META_PIXEL_ID
 *   REACT_APP_GOOGLE_ADS_ID
 *   REACT_APP_GOOGLE_ADS_PURCHASE_LABEL
 */

const GTM_ID = (process.env.REACT_APP_GTM_ID || '').trim();
const GA4_ID = (
  process.env.REACT_APP_GA4_MEASUREMENT_ID ||
  process.env.REACT_APP_FIREBASE_MEASUREMENT_ID ||
  ''
).trim();
const META_PIXEL_ID = (process.env.REACT_APP_META_PIXEL_ID || '').trim();
const GOOGLE_ADS_ID = (process.env.REACT_APP_GOOGLE_ADS_ID || '').trim();
const GOOGLE_ADS_PURCHASE_LABEL = (
  process.env.REACT_APP_GOOGLE_ADS_PURCHASE_LABEL || ''
).trim();

let initialized = false;
const useGtmOnly = Boolean(GTM_ID);

const ensureDataLayer = () => {
  window.dataLayer = window.dataLayer || [];
  return window.dataLayer;
};

const pushDataLayer = (payload) => {
  ensureDataLayer().push(payload);
};

const gtag = (...args) => {
  ensureDataLayer();
  if (typeof window.gtag === 'function') {
    window.gtag(...args);
    return;
  }
  // Queue until the gtag stub / script is ready.
  window.dataLayer.push(args);
};

const loadScript = (src, id) => {
  if (id && document.getElementById(id)) return;
  const script = document.createElement('script');
  script.async = true;
  script.src = src;
  if (id) script.id = id;
  document.head.appendChild(script);
};

const loadGtm = () => {
  if (!GTM_ID || document.getElementById('gtm-script')) return;

  ensureDataLayer().push({
    'gtm.start': Date.now(),
    event: 'gtm.js'
  });

  loadScript(
    `https://www.googletagmanager.com/gtm.js?id=${encodeURIComponent(GTM_ID)}`,
    'gtm-script'
  );

  if (!document.getElementById('gtm-noscript')) {
    const noscript = document.createElement('noscript');
    noscript.id = 'gtm-noscript';
    noscript.innerHTML =
      `<iframe src="https://www.googletagmanager.com/ns.html?id=${GTM_ID}" ` +
      'height="0" width="0" style="display:none;visibility:hidden"></iframe>';
    document.body.insertBefore(noscript, document.body.firstChild);
  }
};

const loadGtagBase = () => {
  if (document.getElementById('gtag-js')) return;
  window.dataLayer = window.dataLayer || [];
  window.gtag = function gtagStub() {
    window.dataLayer.push(arguments);
  };
  gtag('js', new Date());
  loadScript('https://www.googletagmanager.com/gtag/js?id=' + encodeURIComponent(GA4_ID || GOOGLE_ADS_ID), 'gtag-js');
};

const loadGa4 = () => {
  if (!GA4_ID) return;
  loadGtagBase();
  gtag('config', GA4_ID, { send_page_view: false });
};

const loadGoogleAds = () => {
  if (!GOOGLE_ADS_ID) return;
  loadGtagBase();
  gtag('config', GOOGLE_ADS_ID);
};

const loadMetaPixel = () => {
  if (!META_PIXEL_ID || window.fbq) return;

  // Standard Meta Pixel bootstrap.
  /* eslint-disable */
  !(function (f, b, e, v, n, t, s) {
    if (f.fbq) return;
    n = f.fbq = function () {
      n.callMethod ? n.callMethod.apply(n, arguments) : n.queue.push(arguments);
    };
    if (!f._fbq) f._fbq = n;
    n.push = n;
    n.loaded = true;
    n.version = '2.0';
    n.queue = [];
    t = b.createElement(e);
    t.async = true;
    t.src = v;
    s = b.getElementsByTagName(e)[0];
    s.parentNode.insertBefore(t, s);
  })(window, document, 'script', 'https://connect.facebook.net/en_US/fbevents.js');
  /* eslint-enable */

  window.fbq('init', META_PIXEL_ID);
  window.fbq('track', 'PageView');
};

export const initAnalytics = () => {
  if (initialized || typeof window === 'undefined') return;
  initialized = true;

  if (GTM_ID) {
    loadGtm();
  }

  // Direct tags only when GTM is absent, so we don't double-count.
  if (!useGtmOnly) {
    loadGa4();
    loadGoogleAds();
    loadMetaPixel();
  }
};

const toItem = (product, quantity = 1) => ({
  item_id: String(product.id || product.productId || ''),
  item_name: product.name || 'Product',
  item_brand: product.brand || 'VERA by Kamakshi',
  item_category: product.category || undefined,
  price: Number(product.price) || 0,
  quantity: Number(quantity) || 1
});

const trackGa4 = (eventName, params = {}) => {
  pushDataLayer({ ecommerce: null });
  pushDataLayer({
    event: eventName,
    ecommerce: params
  });

  if (!useGtmOnly && GA4_ID) {
    gtag('event', eventName, params);
  }
};

const trackMeta = (eventName, params = {}, eventId) => {
  if (useGtmOnly) {
    // GTM Meta tag should listen for this custom event / dataLayer keys.
    pushDataLayer({
      event: `meta_${eventName}`,
      meta_event_id: eventId,
      meta_params: params
    });
    return;
  }
  if (typeof window.fbq !== 'function') return;
  window.fbq('track', eventName, params, eventId ? { eventID: eventId } : undefined);
};

export const trackPageView = (path, title) => {
  initAnalytics();
  const pagePath = path || window.location.pathname + window.location.search;

  pushDataLayer({
    event: 'page_view',
    page_path: pagePath,
    page_title: title || document.title
  });

  if (!useGtmOnly) {
    if (GA4_ID) {
      gtag('event', 'page_view', {
        page_path: pagePath,
        page_title: title || document.title
      });
    }
    if (typeof window.fbq === 'function') {
      window.fbq('track', 'PageView');
    }
  }
};

export const trackViewItem = (product) => {
  if (!product?.id) return;
  initAnalytics();
  const item = toItem(product);
  const value = item.price;

  trackGa4('view_item', {
    currency: 'INR',
    value,
    items: [item]
  });

  trackMeta('ViewContent', {
    content_ids: [item.item_id],
    content_type: 'product',
    content_name: item.item_name,
    value,
    currency: 'INR'
  });
};

export const trackAddToCart = (product, quantity = 1) => {
  if (!product?.id) return;
  initAnalytics();
  const item = toItem(product, quantity);
  const value = item.price * item.quantity;

  trackGa4('add_to_cart', {
    currency: 'INR',
    value,
    items: [item]
  });

  trackMeta('AddToCart', {
    content_ids: [item.item_id],
    content_type: 'product',
    content_name: item.item_name,
    value,
    currency: 'INR',
    contents: [{ id: item.item_id, quantity: item.quantity }]
  });
};

export const trackBeginCheckout = ({ items = [], value = 0, coupon } = {}) => {
  initAnalytics();
  const mapped = items.map((item) => toItem(item, item.quantity));
  const total = Number(value) || mapped.reduce((sum, i) => sum + i.price * i.quantity, 0);

  trackGa4('begin_checkout', {
    currency: 'INR',
    value: total,
    coupon: coupon || undefined,
    items: mapped
  });

  trackMeta('InitiateCheckout', {
    content_ids: mapped.map((i) => i.item_id),
    content_type: 'product',
    value: total,
    currency: 'INR',
    num_items: mapped.reduce((sum, i) => sum + i.quantity, 0),
    contents: mapped.map((i) => ({ id: i.item_id, quantity: i.quantity }))
  });
};

/**
 * Fire once per order. Pass a stable eventId (order id) so Meta browser + CAPI
 * can dedupe.
 */
export const trackPurchase = ({
  orderId,
  value = 0,
  items = [],
  shipping = 0,
  tax = 0,
  coupon
} = {}) => {
  if (!orderId) return;
  initAnalytics();

  const storageKey = `purchase_tracked_${orderId}`;
  try {
    if (sessionStorage.getItem(storageKey)) return;
    sessionStorage.setItem(storageKey, '1');
  } catch (_) {
    // sessionStorage may be unavailable; continue and risk a rare double fire
  }

  const mapped = items.map((item) => toItem(item, item.quantity));
  const total = Number(value) || 0;
  const eventId = String(orderId);

  trackGa4('purchase', {
    transaction_id: eventId,
    currency: 'INR',
    value: total,
    shipping: Number(shipping) || 0,
    tax: Number(tax) || 0,
    coupon: coupon || undefined,
    items: mapped
  });

  trackMeta(
    'Purchase',
    {
      content_ids: mapped.map((i) => i.item_id),
      content_type: 'product',
      value: total,
      currency: 'INR',
      contents: mapped.map((i) => ({ id: i.item_id, quantity: i.quantity })),
      num_items: mapped.reduce((sum, i) => sum + i.quantity, 0)
    },
    eventId
  );

  if (!useGtmOnly && GOOGLE_ADS_ID && GOOGLE_ADS_PURCHASE_LABEL) {
    gtag('event', 'conversion', {
      send_to: `${GOOGLE_ADS_ID}/${GOOGLE_ADS_PURCHASE_LABEL}`,
      value: total,
      currency: 'INR',
      transaction_id: eventId
    });
  } else if (GOOGLE_ADS_ID && GOOGLE_ADS_PURCHASE_LABEL) {
    pushDataLayer({
      event: 'ads_conversion_purchase',
      transaction_id: eventId,
      value: total,
      currency: 'INR'
    });
  }
};

export const analyticsConfig = {
  gtmId: GTM_ID || null,
  ga4Id: GA4_ID || null,
  metaPixelId: META_PIXEL_ID || null,
  googleAdsId: GOOGLE_ADS_ID || null,
  ready: Boolean(GTM_ID || GA4_ID || META_PIXEL_ID || GOOGLE_ADS_ID)
};
